/**
 * `useFeedbackZone` — the feedback hook for app code that pushes items.
 *
 * Returns ONLY the stable actions (push, dismiss, dismissAll). zustand's
 * actions are reference-stable, so `zone` is safe to put in effect deps.
 * The full state (including `items`) is intentionally not exposed here — a
 * snapshot that changes on every push would loop any effect that lists
 * `zone` as a dep. Code that needs `items` should read them directly via
 * `useFeedbackStore`.
 *
 * ## Where a pushed message goes
 *
 * Feedback is delivered to the surface that owns the interaction:
 *
 * - **Inside a dialog** → that dialog's own inline band. A modal is the user's
 *   whole world while it's open; answering an action taken inside one with a
 *   banner pinned to the bottom of the app breaks the action→result loop (and
 *   before this, that banner painted behind the overlay anyway).
 * - **Anywhere else** → the global banner, exactly as before.
 *
 * Resolution follows the React tree, which means it follows *ownership*, not
 * whichever dialog happens to be on top. In the profile flow the delete
 * mutation belongs to `ContactMethodsEditor`, which lives inside
 * `ProfileEditDialog` and not inside the confirm it opens — so its error lands
 * on the profile dialog. That's the surface the request came from, so that's
 * the surface that answers.
 *
 * Callers don't opt in. `useApiMutation` and `useAsyncAction` both route
 * through this hook, so every existing push site is already correct.
 */
import { useMemo } from 'react';
import { useFeedbackStore, type FeedbackState } from '@/store/useFeedbackStore';
import { useDialogLayer } from '@/components/dialogs/StyledDialog/DialogLayerContext';
import type { FeedbackLayerSink } from '@/api/feedbackLayerMeta';

export type { FeedbackItem, FeedbackSeverity } from '@/store/useFeedbackStore';
/**
 * `push` is the layer-sink signature (#1174): its third argument is a dialog
 * opening (a number, ignored by the global banner) or the store's push options.
 */
export type FeedbackZoneContextValue = Pick<FeedbackState, 'dismiss' | 'dismissAll'> & Pick<FeedbackLayerSink, 'push' | 'clearSource'>;

export function useFeedbackZone(): FeedbackZoneContextValue {
  const push = useFeedbackStore((s) => s.push);
  const dismiss = useFeedbackStore((s) => s.dismiss);
  const dismissAll = useFeedbackStore((s) => s.dismissAll);
  const clearSource = useFeedbackStore((s) => s.clearSource);
  const layer = useDialogLayer();

  return useMemo(
    () =>
      layer ?? {
        // A dialog opening means nothing to the global banner: only options pass through.
        push: (message, severity, openingOrOptions) =>
          push(message, severity, typeof openingOrOptions === 'object' ? openingOrOptions : undefined),
        clearSource,
        dismiss,
        dismissAll,
      },
    [layer, push, dismiss, dismissAll, clearSource],
  );
}
